import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { createContext, Fragment, ReactNode, useContext } from 'react';
import { getChildrenByType } from '../../utils';
import { TooltipV2 } from '../tooltip/TooltipV2';
import Spinner from '../spinner/Spinner';
import ButtonV2 from './ButtonV2';

const buttonStyles = {
    base: 'focus:outline-none focus:ring-0 disabled:cursor-default disabled:opacity-50',
    type: {
        primary:
            'border border-transparent font-medium text-white dark:text-neutral-50 bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 active:bg-indigo-800 dark:active:bg-indigo-700',
        secondary:
            'border border-gray-300 dark:border-white/[0.05] dark:hover:border-white/[0.07] dark:active:border-white/[0.11] font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-white/[0.05] hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.3] dark:disabled:bg-white/[0.1]',
        hollow: 'border border-transparent font-medium text-gray-700 dark:text-gray-200 dark:bg-white/[0.0] hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.11]',
        danger: 'border border-transparent font-medium text-white dark:text-neutral-50 bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 active:bg-red-800 dark:active:bg-red-700',
    },
    size: {
        xs: 'px-2.5 py-2 text-xs',
        sm: 'px-2.5 py-2 text-sm',
        base: 'px-2.5 py-2 text-base',
        lg: 'px-2.5 py-2 text-lg',
        xl: 'px-2.5 py-2 text-xl',
    },
    rounding: {
        xs: 'rounded-l',
        sm: 'rounded-l-md',
        base: 'rounded-l-md',
        lg: 'rounded-l-md',
        xl: 'rounded-l-md',
    },
};

const dropdownStyles = {
    type: {
        primary:
            'border border-transparent border-l-white font-medium text-white dark:text-neutral-50 bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 active:bg-indigo-800 dark:active:bg-indigo-700',
        secondary:
            'border border-gray-300 dark:border-white/[0.05] dark:hover:border-white/[0.07] dark:active:border-white/[0.11] font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-white/[0.05] hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.3] dark:disabled:bg-white/[0.1]',
        hollow: 'border border-transparent font-medium text-gray-700 dark:text-gray-200 dark:bg-white/[0.0] hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.11]',
        danger: 'border border-transparent font-medium text-white dark:text-neutral-50 bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 active:bg-red-800 dark:active:bg-red-700',
    },
    padding: {
        xs: 'px-1 py-1.5',
        sm: 'px-2 py-2',
        base: 'px-2 py-2',
        lg: 'px-3 py-2',
        xl: 'px-4 py-3',
    },
    iconDimension: {
        xs: 'h-3 w-3',
        sm: 'h-3.5 w-3.5',
        base: 'h-4 w-4',
        lg: 'h-5 w-5',
        xl: 'h-6 w-6',
    },
    iconColour: {
        primary: 'text-white',
        secondary: 'text-indigo-600 dark:text-indigo-400',
        hollow: 'text-indigo-600 dark:text-indigo-400',
        danger: 'text-white',
    },
    rounding: {
        xs: 'rounded-r',
        sm: 'rounded-r-md',
        base: 'rounded-r-md',
        lg: 'rounded-r-md',
        xl: 'rounded-r-md',
    },
};

export type ButtonGroupProps = {
    type?: 'primary' | 'secondary' | 'hollow';
    size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
    children?: ReactNode | ReactNode[];
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    isDisabled?: boolean;
    disabledHint?: string;
    isLoading?: boolean;
    label: string;
    leftIcon?: ReactNode;
    onClick: () => void;
};

const ButtonGroupContext = createContext<Pick<ButtonGroupProps, 'size'>>({ size: 'base' });

function ButtonGroup({
    type,
    size = 'base',
    tooltipPosition = 'right',
    isDisabled = false,
    disabledHint,
    children,
    label,
    isLoading,
    leftIcon,
    onClick,
}: ButtonGroupProps): JSX.Element {
    const _children = getChildrenByType(children, SecondaryAction);

    if (_children.length === 0) {
        return (
            <ButtonV2
                type={type}
                size={size}
                tooltipPosition={tooltipPosition}
                disabledHint={disabledHint}
                isDisabled={isDisabled}
                isLoading={isLoading}
                label={label}
                leftIcon={leftIcon}
                onClick={onClick}
            />
        );
    }

    return (
        <TooltipV2 position={tooltipPosition} message={disabledHint} disabled={!disabledHint || !isDisabled}>
            <div className="relative inline-flex">
                <button
                    type="button"
                    disabled={isDisabled}
                    onClick={onClick}
                    className={`flex items-center gap-x-2 ${buttonStyles.base} ${buttonStyles.type[type]} ${buttonStyles.size[size]} ${buttonStyles.rounding[size]}`}
                >
                    {isLoading && <Spinner size="sm" />}
                    {leftIcon && (
                        <span className={`${dropdownStyles.iconDimension[size]} ${dropdownStyles.iconColour[type]}`}>{leftIcon}</span>
                    )}
                    <p className="leading-none">{label}</p>
                </button>
                <ButtonGroupContext.Provider value={{ size }}>
                    <Menu as="div" className="-ml-px relative block">
                        <Menu.Button
                            disabled={isDisabled}
                            className={`relative h-full inline-flex items-center ${dropdownStyles.type[type]} ${dropdownStyles.padding[size]} ${dropdownStyles.rounding[size]} ${buttonStyles.base}`}
                        >
                            <span className="sr-only">Open options</span>
                            <ChevronDownIcon className={dropdownStyles.iconDimension[size]} aria-hidden="true" />
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="flex flex-col z-50 py-1 origin-top-right absolute right-0 mt-2 w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {_children}
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </ButtonGroupContext.Provider>
            </div>
        </TooltipV2>
    );
}

type SecondaryActionProps = {
    label: string;
    disabled?: boolean;
    disabledHint?: string;
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    onClick: () => void;
};

function SecondaryAction({
    label,
    disabled = false,
    disabledHint,
    tooltipPosition,
    leftIcon,
    rightIcon,
    onClick,
}: SecondaryActionProps): JSX.Element {
    const context = useContext(ButtonGroupContext);

    const styles = {
        textSize: {
            xs: 'text-xs',
            sm: 'text-sm',
            base: 'text-sm',
            lg: 'text-base',
            xl: 'text-base',
        },

        iconDimension: {
            xs: 'h-3.5 w-3.5',
            sm: 'h-3.5 w-3.5',
            base: 'h-4 w-4',
            lg: 'h-5 w-5',
            xl: 'h-6 w-6',
        },
    };

    return (
        <TooltipV2 message={disabledHint} disabled={!disabledHint} position={tooltipPosition}>
            <Menu.Item>
                <button
                    onClick={onClick}
                    type="button"
                    className="focus:outline-none focus:ring-0 disabled:cursor-default disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.11] dark:disabled:bg-white/[0.0]"
                    disabled={disabled}
                >
                    <span
                        className={`flex items-center rounded-[5px] gap-x-3.5 ${
                            styles.textSize[context.size]
                        } font-normal text-gray-700 dark:text-neutral-200 px-3 py-2`}
                    >
                        {leftIcon && (
                            <span className={`${styles.iconDimension[context.size]} text-indigo-600 dark:text-indigo-400`}>{leftIcon}</span>
                        )}
                        {label}
                        {rightIcon && (
                            <span className={`${styles.iconDimension[context.size]} text-indigo-600 dark:text-indigo-400`}>
                                {rightIcon}
                            </span>
                        )}
                    </span>
                </button>
            </Menu.Item>
        </TooltipV2>
    );
}

ButtonGroup.SecondaryAction = SecondaryAction;

export default ButtonGroup;
